import { Injectable } from "@angular/core";
import { coerceTimeFormat } from "@app/_helpers/utils";
import { ComegoService, ComegoTime, ComegoTimeType } from "@timeghost/web-api";
import {
	addDays,
	addHours,
	addMilliseconds,
	addMinutes,
	clamp as clampDate,
	endOfDay,
	isSameDay,
	roundToNearestMinutes,
	startOfDay,
} from "date-fns/esm";

export interface ComegoScheduleLike {
	enabled?: boolean;
	items?: Array<{
		day: number;
		enabled?: boolean;
		items?: Array<{ from?: string; to?: string }>;
	}>;
}

export interface WorkingHoursLike {
	breaks?: Array<{ workingHours: number; minutesDue: number }>;
	/** Max working hours per day (in hours). Used to clamp suggested work segment end. */
	dailyMaxHours?: number;
	/** Minimum break duration (in hours) for a pause to count toward break rules. From workspace settings. */
	minimumBreakDurationForCredit?: number;
}

export interface ComegoSuggestionParams {
	date: Date;
	existingEntries: ComegoTime[];
	/** Form rows (start/end as "HH:mm") so break rules can use work + pause in sum for the day. */
	formEntries?: Array<{ start: string; end: string; type: ComegoTimeType }>;
	lastFormEntry?: { endTs: number; type: ComegoTimeType };
	newWorkEntryDurationMs?: number | null;
	/** When set, use this type instead of deriving from last form/existing (e.g. user chose type explicitly). */
	preferredType?: ComegoTimeType;
	schedule: ComegoScheduleLike | null;
	workingHours?: WorkingHoursLike | null;
}

export interface ComegoSuggestionResult {
	endTime: Date;
	startTime: Date;
	type: ComegoTimeType;
}

@Injectable({ providedIn: "root" })
export class ComegoSuggestionService {
	constructor(private comegoService: ComegoService) {}

	/**
	 * Fetches last work entry duration from the API (past `daysBack` days) so entries from any week are considered.
	 * Single source for "last work duration" in the app; avoids relying on the store which only holds the current week.
	 */
	async getLastWorkEntryDurationMs(userId: string, daysBack: number = 7): Promise<number | null> {
		const now = new Date();
		const rangeStart = addDays(now, -daysBack);
		const entries = await this.comegoService.getBetween(rangeStart, now, userId).catch((): ComegoTime[] => []);
		const workEntries = (entries ?? []).filter(
			(e) => e.type === "work" && e.start && e.end && e.user?.id === userId
		);
		if (workEntries.length === 0) return null;
		const last = workEntries.sort((a, b) => new Date(b.start).getTime() - new Date(a.start).getTime())[0];
		return new Date(last.end).getTime() - new Date(last.start).getTime();
	}

	getFirstScheduleStartTime(schedule: ComegoScheduleLike | null, date: Date): Date | null {
		if (!schedule?.enabled || !schedule.items?.length) return null;
		const dayOfWeek = startOfDay(date).getUTCDay();
		const daySchedule = schedule.items.find((item) => item.day === dayOfWeek && item.enabled);
		if (!daySchedule?.items?.length) return null;
		const firstWorkPeriod = daySchedule.items[0];
		if (!firstWorkPeriod?.from) return null;
		return coerceTimeFormat(firstWorkPeriod.from, date);
	}

	getNextScheduleEndTime(
		schedule: ComegoScheduleLike | null,
		date: Date,
		time: Date,
		workingHours?: WorkingHoursLike | null
	): Date | null {
		if (!schedule?.enabled || !schedule.items?.length) return null;
		const dayOfWeek = startOfDay(date).getUTCDay();
		const daySchedule = schedule.items.find((item) => item.day === dayOfWeek && item.enabled);
		if (!daySchedule?.items?.length) return null;
		for (const period of daySchedule.items) {
			if (!period.from || !period.to) continue;
			const periodStart = coerceTimeFormat(period.from, date);
			const periodEnd = coerceTimeFormat(period.to, date);
			if ((periodStart <= time && time < periodEnd) || time <= periodStart) {
				let result = periodEnd;
				if (workingHours?.breaks?.length) {
					const earliestBreakHours = Math.min(...workingHours.breaks.map((b) => b.workingHours));
					const maxSegmentEnd = addHours(time, earliestBreakHours);
					if (maxSegmentEnd < result) result = maxSegmentEnd;
				}
				return result;
			}
		}
		return null;
	}

	getNextScheduleStartTime(schedule: ComegoScheduleLike | null, date: Date, time: Date): Date | null {
		if (!schedule?.enabled || !schedule.items?.length) return null;
		const dayOfWeek = startOfDay(date).getUTCDay();
		const daySchedule = schedule.items.find((item) => item.day === dayOfWeek && item.enabled);
		if (!daySchedule?.items?.length) return null;
		for (const period of daySchedule.items) {
			if (!period.from) continue;
			const periodStart = coerceTimeFormat(period.from, date);
			if (periodStart > time) return periodStart;
		}
		return null;
	}

	/**
	 * Checks if the given time is within or after the last scheduled work period of the day.
	 * @returns true if time is in or after the last work period, false otherwise
	 */
	isInOrAfterLastScheduledWorkPeriod(schedule: ComegoScheduleLike | null, date: Date, time: Date): boolean {
		if (!schedule?.enabled || !schedule.items?.length) return true;
		const dayOfWeek = startOfDay(date).getUTCDay();
		const daySchedule = schedule.items.find((item) => item.day === dayOfWeek && item.enabled);
		if (!daySchedule?.items?.length) return true;

		const workPeriods = daySchedule.items
			.filter((p) => p.from && p.to)
			.map((p) => ({
				start: coerceTimeFormat(p.from, date),
				end: coerceTimeFormat(p.to, date),
			}))
			.sort((a, b) => a.start.getTime() - b.start.getTime());

		if (workPeriods.length === 0) return true;

		const lastPeriod = workPeriods[workPeriods.length - 1];
		return time >= lastPeriod.start;
	}

	/**
	 * Calculates the total target hours for a given day based on the schedule.
	 * Target hours are the sum of all scheduled work periods for that day.
	 * @returns Total target hours in milliseconds, or null if no schedule or day not enabled
	 */
	getTargetHoursForDay(schedule: ComegoScheduleLike | null, date: Date): number | null {
		if (!schedule?.enabled || !schedule.items?.length) return null;
		const dayOfWeek = startOfDay(date).getUTCDay();
		const daySchedule = schedule.items.find((item) => item.day === dayOfWeek && item.enabled);
		if (!daySchedule?.items?.length) return null;

		let totalMs = 0;
		for (const period of daySchedule.items) {
			if (!period.from || !period.to) continue;
			const periodStart = coerceTimeFormat(period.from, date);
			const periodEnd = coerceTimeFormat(period.to, date);
			const durationMs = Math.max(0, periodEnd.getTime() - periodStart.getTime());
			totalMs += durationMs;
		}

		return totalMs > 0 ? totalMs : null;
	}

	/**
	 * Calculates remaining work time needed to reach the target hours for a given day.
	 * Only counts work-type entries (pauses are excluded).
	 * @returns Remaining milliseconds to reach target hours, or null if target already reached or no schedule
	 */
	getRemainingTargetHoursForDay(
		schedule: ComegoScheduleLike | null,
		date: Date,
		existingEntries: ComegoTime[],
		formEntries: Array<{ start: string; end: string; type: ComegoTimeType }>
	): number | null {
		const targetMs = this.getTargetHoursForDay(schedule, date);
		if (targetMs === null) return null;

		const existingForDay = existingEntries.filter((e) => e.start && e.end && isSameDay(new Date(e.start), date));
		let totalWorkMs = existingForDay.reduce((sum, e) => {
			if (e.type !== "work") return sum;
			return sum + Math.max(0, new Date(e.end).getTime() - new Date(e.start).getTime());
		}, 0);

		for (const f of formEntries ?? []) {
			if (f.type !== "work" || !f.start || !f.end) continue;
			const start = coerceTimeFormat(f.start, date);
			const end = coerceTimeFormat(f.end, date);
			totalWorkMs += Math.max(0, end.getTime() - start.getTime());
		}

		const remainingMs = targetMs - totalWorkMs;
		return remainingMs > 0 ? remainingMs : null;
	}

	/**
	 * Calculates remaining work time to reach the daily maximum hours.
	 * Only counts work-type entries (pauses are excluded).
	 * @returns Remaining milliseconds to reach daily max, or null if max already reached or no daily max configured
	 */
	getRemainingMaxHoursForDay(
		date: Date,
		existingEntries: ComegoTime[],
		formEntries: Array<{ start: string; end: string; type: ComegoTimeType }>,
		workingHours?: WorkingHoursLike | null
	): number | null {
		if (typeof workingHours?.dailyMaxHours !== "number" || workingHours.dailyMaxHours <= 0) return null;

		const existingForDay = existingEntries.filter((e) => e.start && e.end && isSameDay(new Date(e.start), date));
		let totalWorkMs = existingForDay.reduce((sum, e) => {
			if (e.type !== "work") return sum;
			return sum + Math.max(0, new Date(e.end).getTime() - new Date(e.start).getTime());
		}, 0);

		for (const f of formEntries ?? []) {
			if (f.type !== "work" || !f.start || !f.end) continue;
			const start = coerceTimeFormat(f.start, date);
			const end = coerceTimeFormat(f.end, date);
			totalWorkMs += Math.max(0, end.getTime() - start.getTime());
		}

		const dailyMaxMs = workingHours.dailyMaxHours * 3600 * 1000;
		const remainingMs = dailyMaxMs - totalWorkMs;
		return remainingMs > 0 ? remainingMs : null;
	}

	/**
	 * Calculates the required break duration based on work hours completed and break rules.
	 * @returns Required break duration in minutes, or null if no break rules apply
	 */
	getRequiredBreakDuration(
		date: Date,
		existingEntries: ComegoTime[],
		formEntries: Array<{ start: string; end: string; type: ComegoTimeType }>,
		workingHours?: WorkingHoursLike | null
	): number | null {
		if (!workingHours?.breaks?.length) return null;

		const existingForDay = existingEntries.filter((e) => e.start && e.end && isSameDay(new Date(e.start), date));
		const minBreakDurationForCreditMs = (workingHours.minimumBreakDurationForCredit ?? 0.25) * 3.6e6;

		let totalWorkMs = 0;
		let totalCreditedPauseMs = 0;
		let continuousWorkMs = 0;

		const merged = [
			...existingForDay.map((e) => ({ start: new Date(e.start), end: new Date(e.end), type: e.type })),
			...(formEntries ?? []).map((f) => ({
				start: coerceTimeFormat(f.start, date),
				end: coerceTimeFormat(f.end, date),
				type: f.type,
			})),
		].sort((a, b) => a.start.getTime() - b.start.getTime());

		for (const e of merged) {
			const dur = Math.max(0, e.end.getTime() - e.start.getTime());
			if (e.type === "work") {
				totalWorkMs += dur;
				continuousWorkMs += dur;
			} else if (e.type === "pause" && dur >= minBreakDurationForCreditMs) {
				continuousWorkMs = 0;
				totalCreditedPauseMs += dur;
			}
		}

		const sortedRules = [...workingHours.breaks].sort((a, b) => b.workingHours - a.workingHours);

		for (const rule of sortedRules) {
			const workingHoursMs = rule.workingHours * 3600 * 1000;
			const requiredPauseMs = rule.minutesDue * 3600 * 1000;

			if (continuousWorkMs >= workingHoursMs) {
				return Math.round(rule.minutesDue * 60);
			}

			if (totalWorkMs >= workingHoursMs && totalCreditedPauseMs < requiredPauseMs) {
				const remainingBreakMs = requiredPauseMs - totalCreditedPauseMs;
				return Math.round(remainingBreakMs / (60 * 1000));
			}
		}

		return null;
	}

	/**
	 * Latest end time for an open work segment that still complies with break rules.
	 * Prior same-day entries (excluding the open segment) establish continuous/total work state.
	 */
	getMaxCompliantWorkEndForOpenSegment(
		date: Date,
		segmentStart: Date,
		segmentEnd: Date,
		existingForDay: ComegoTime[],
		formEntries: Array<{ start: string; end: string; type: ComegoTimeType }>,
		workingHours: WorkingHoursLike | null | undefined,
		options?: { includeDailyMax?: boolean }
	): Date {
		const merged: Array<{ start: Date; end: Date; type: ComegoTimeType }> = [
			...existingForDay.map((e) => ({ start: new Date(e.start), end: new Date(e.end), type: e.type })),
			...formEntries.map((f) => ({
				start: coerceTimeFormat(f.start, date),
				end: coerceTimeFormat(f.end, date),
				type: f.type,
			})),
		].sort((a, b) => a.start.getTime() - b.start.getTime());

		const minBreakDurationForCreditMs = (workingHours?.minimumBreakDurationForCredit ?? 0.25) * 3.6e6;
		let totalWorkMs = 0;
		let totalCreditedPauseMs = 0;
		let continuousWorkMs = 0;
		for (const e of merged) {
			const dur = Math.max(0, e.end.getTime() - e.start.getTime());
			if (e.type === "work") {
				totalWorkMs += dur;
				continuousWorkMs += dur;
			} else if (e.type === "pause" && dur >= minBreakDurationForCreditMs) {
				continuousWorkMs = 0;
				totalCreditedPauseMs += dur;
			}
		}

		const segmentDurationMs = Math.max(0, segmentEnd.getTime() - segmentStart.getTime());
		let resultEnd = segmentEnd.getTime();

		// Only clamp when this segment would *first* exceed a limit; once at/over the limit, suggest normally so the next entry is not repeatedly clamped.
		if (workingHours?.breaks?.length) {
			const rules = [...workingHours.breaks].sort((a, b) => b.workingHours - a.workingHours);
			for (const rule of rules) {
				const workingHoursMs = rule.workingHours * 3600 * 1000;
				const requiredPauseMs = rule.minutesDue * 3600 * 1000;

				// Check continuous work limit (e.g., can't work more than 6h continuously)
				const wouldExceedContinuous = continuousWorkMs + segmentDurationMs > workingHoursMs;
				const notYetAtContinuousLimit = continuousWorkMs < workingHoursMs;
				if (wouldExceedContinuous && notYetAtContinuousLimit) {
					const maxWorkMs = workingHoursMs - continuousWorkMs;
					const maxEnd = segmentStart.getTime() + Math.max(0, maxWorkMs);
					if (maxEnd < resultEnd) resultEnd = maxEnd;
				}

				// Check total work vs total break requirement (e.g., 9h work needs 45min total breaks)
				const wouldExceedTotalWithoutBreaks =
					totalCreditedPauseMs < requiredPauseMs && totalWorkMs + segmentDurationMs > workingHoursMs;
				const notYetAtTotalLimit = totalWorkMs < workingHoursMs;
				if (wouldExceedTotalWithoutBreaks && notYetAtTotalLimit) {
					const maxWorkMs = workingHoursMs - totalWorkMs;
					const maxEnd = segmentStart.getTime() + Math.max(0, maxWorkMs);
					if (maxEnd < resultEnd) resultEnd = maxEnd;
				}
			}
		}

		if (
			options?.includeDailyMax !== false &&
			typeof workingHours?.dailyMaxHours === "number" &&
			workingHours.dailyMaxHours > 0
		) {
			const dailyMaxMs = workingHours.dailyMaxHours * 3600 * 1000;
			const notYetAtDailyMax = totalWorkMs < dailyMaxMs;
			if (notYetAtDailyMax) {
				const maxWorkRemainingMs = Math.max(0, dailyMaxMs - totalWorkMs);
				const maxEndByDay = segmentStart.getTime() + maxWorkRemainingMs;
				if (maxEndByDay < resultEnd) resultEnd = maxEndByDay;
			}
		}

		const dayEndMs = endOfDay(date).getTime();
		if (resultEnd > dayEndMs) resultEnd = dayEndMs;

		return new Date(resultEnd);
	}

	private clampToDay(date: Date, time: Date): Date {
		return clampDate(time, { start: startOfDay(date), end: endOfDay(date) });
	}

	/**
	 * Clamps work segment end by break rules (after Xh work need Y min break).
	 * Only breaks with duration >= minimumBreakDurationForCredit (workspace setting) count toward the required pause.
	 * Also clamps by daily max working hours when set.
	 */
	private clampWorkEndByRules(
		date: Date,
		segmentStart: Date,
		segmentEnd: Date,
		existingForDay: ComegoTime[],
		formEntries: Array<{ start: string; end: string; type: ComegoTimeType }>,
		workingHours: WorkingHoursLike | null | undefined
	): Date {
		return this.getMaxCompliantWorkEndForOpenSegment(
			date,
			segmentStart,
			segmentEnd,
			existingForDay,
			formEntries,
			workingHours,
			{ includeDailyMax: true }
		);
	}

	isTimeInScheduleGap(schedule: ComegoScheduleLike | null, date: Date, time: Date): boolean {
		const nextStart = this.getNextScheduleStartTime(schedule, date, time);
		if (!nextStart || nextStart <= time) return false;
		if (!schedule?.enabled || !schedule.items?.length) return false;
		const dayOfWeek = startOfDay(date).getUTCDay();
		const daySchedule = schedule.items.find((item) => item.day === dayOfWeek && item.enabled);
		if (!daySchedule?.items?.length) return false;
		for (const period of daySchedule.items) {
			if (!period.from || !period.to) continue;
			const periodStart = coerceTimeFormat(period.from, date);
			const periodEnd = coerceTimeFormat(period.to, date);
			if (time >= periodStart && time < periodEnd) return false;
		}
		return true;
	}

	/**
	 * Returns true when the existing entries for the given date already fulfill or exceed
	 * the enforced daily max work duration (workingHours.dailyMaxHours). Only work-type
	 * entries are counted.
	 */
	isDailyMaxReachedForDate(
		date: Date,
		existingEntries: ComegoTime[],
		workingHours?: WorkingHoursLike | null
	): boolean {
		if (typeof workingHours?.dailyMaxHours !== "number" || workingHours.dailyMaxHours <= 0) return false;
		const existingForDay = existingEntries.filter((e) => e.start && e.end && isSameDay(new Date(e.start), date));
		const totalWorkMs = existingForDay.reduce((sum, e) => {
			if (e.type !== "work") return sum;
			return sum + Math.max(0, new Date(e.end).getTime() - new Date(e.start).getTime());
		}, 0);
		const dailyMaxMs = workingHours.dailyMaxHours * 3600 * 1000;
		return totalWorkMs >= dailyMaxMs;
	}

	/**
	 * Returns true when existing entries plus form entries (for the given date) combined
	 * would meet or exceed the enforced daily max work duration. Used to disable adding
	 * more work entries when the total would exceed the limit.
	 */
	isDailyMaxReachedOrExceededByCombined(
		date: Date,
		existingEntries: ComegoTime[],
		formEntries: Array<{ start: string; end: string; type: ComegoTimeType }>,
		workingHours?: WorkingHoursLike | null
	): boolean {
		if (typeof workingHours?.dailyMaxHours !== "number" || workingHours.dailyMaxHours <= 0) return false;
		const existingForDay = existingEntries.filter((e) => e.start && e.end && isSameDay(new Date(e.start), date));
		let totalWorkMs = existingForDay.reduce((sum, e) => {
			if (e.type !== "work") return sum;
			return sum + Math.max(0, new Date(e.end).getTime() - new Date(e.start).getTime());
		}, 0);
		for (const f of formEntries ?? []) {
			if (f.type !== "work" || !f.start || !f.end) continue;
			const start = coerceTimeFormat(f.start, date);
			const end = coerceTimeFormat(f.end, date);
			totalWorkMs += Math.max(0, end.getTime() - start.getTime());
		}
		const dailyMaxMs = workingHours.dailyMaxHours * 3600 * 1000;
		return totalWorkMs >= dailyMaxMs;
	}

	getSuggestedNextEntry(params: ComegoSuggestionParams): ComegoSuggestionResult {
		const {
			date,
			existingEntries,
			formEntries,
			lastFormEntry,
			schedule,
			workingHours,
			newWorkEntryDurationMs,
			preferredType,
		} = params;
		const existingForDay = existingEntries.filter((e) => e.start && isSameDay(new Date(e.start), date));
		const lastExistingByTime =
			existingForDay.length > 0
				? existingForDay.reduce((a, b) => (new Date(a.end).getTime() >= new Date(b.end).getTime() ? a : b))
				: null;
		const type: ComegoTimeType =
			preferredType ??
			(lastFormEntry
				? lastFormEntry.type === "work"
					? "pause"
					: "work"
				: lastExistingByTime
					? lastExistingByTime.type === "work"
						? "pause"
						: "work"
					: "work");

		const dayEnd = endOfDay(date);

		let startTime: Date = lastFormEntry
			? new Date(lastFormEntry.endTs)
			: lastExistingByTime
				? new Date(lastExistingByTime.end)
				: (this.getFirstScheduleStartTime(schedule, date) ??
					roundToNearestMinutes(new Date(), { nearestTo: 15 }));
		startTime = this.clampToDay(date, startTime);

		let endTime: Date;
		if (type === "work") {
			const scheduleEnd = this.getNextScheduleEndTime(schedule, date, startTime, workingHours);
			const isLastPeriod = this.isInOrAfterLastScheduledWorkPeriod(schedule, date, startTime);

			if (scheduleEnd && !isLastPeriod) {
				// Within schedule but not last period - use schedule end time
				endTime = scheduleEnd;
			} else {
				// Last period or outside schedule - check target hours to compensate
				const remainingTargetMs = this.getRemainingTargetHoursForDay(
					schedule,
					date,
					existingForDay,
					formEntries ?? []
				);

				if (remainingTargetMs !== null && remainingTargetMs > 0) {
					// Target hours not yet reached - suggest duration to reach target
					endTime = clampDate(addMilliseconds(startTime, remainingTargetMs), {
						start: startTime,
						end: dayEnd,
					});
				} else {
					// Target hours reached - check daily max
					const remainingMaxMs = this.getRemainingMaxHoursForDay(
						date,
						existingForDay,
						formEntries ?? [],
						workingHours
					);

					if (remainingMaxMs !== null && remainingMaxMs > 0) {
						// Suggest duration to reach daily max hours
						endTime = clampDate(addMilliseconds(startTime, remainingMaxMs), {
							start: startTime,
							end: dayEnd,
						});
					} else {
						// No target or max, or both reached - use default duration
						endTime = newWorkEntryDurationMs
							? clampDate(addMilliseconds(startTime, newWorkEntryDurationMs), {
									start: startTime,
									end: dayEnd,
								})
							: addMinutes(startTime, 30);
					}
				}
			}

			// Clamp by break rules
			endTime = this.clampWorkEndByRules(
				date,
				startTime,
				endTime,
				existingForDay,
				formEntries ?? [],
				workingHours
			);
		} else if (type === "pause") {
			const nextPeriodStart = this.getNextScheduleStartTime(schedule, date, startTime);
			const minPauseMinutesForCredit = Math.round((workingHours?.minimumBreakDurationForCredit ?? 0.25) * 60);

			// Check if standard break rules are configured (30min after 6h, 45min after 9h)
			const hasStandardBreakRules =
				workingHours?.breaks?.some((b) => b.workingHours === 6 && b.minutesDue === 0.5) &&
				workingHours?.breaks?.some((b) => b.workingHours === 9 && b.minutesDue === 0.75);

			// Check if there's already a 30-minute break for the day
			const minBreakDurationForCreditMs = (workingHours?.minimumBreakDurationForCredit ?? 0.25) * 3.6e6;
			const hasRequiredBreak = [
				...existingForDay,
				...(formEntries ?? []).map((f) => ({
					start: coerceTimeFormat(f.start, date),
					end: coerceTimeFormat(f.end, date),
					type: f.type,
				})),
			].some((e) => {
				if (e.type !== "pause") return false;
				const dur = Math.max(0, new Date(e.end).getTime() - new Date(e.start).getTime());
				return dur >= minBreakDurationForCreditMs && dur >= 30 * 60 * 1000;
			});

			// Determine break duration
			let breakMinutes: number;
			const inScheduleGap = this.isTimeInScheduleGap(schedule, date, startTime);
			const requiredBreakMinutes = this.getRequiredBreakDuration(
				date,
				existingForDay,
				formEntries ?? [],
				workingHours
			);

			if (nextPeriodStart && nextPeriodStart > startTime && inScheduleGap) {
				const gapMinutes = (nextPeriodStart.getTime() - startTime.getTime()) / (60 * 1000);

				if (requiredBreakMinutes !== null && requiredBreakMinutes > gapMinutes) {
					breakMinutes = requiredBreakMinutes;
				} else {
					breakMinutes = gapMinutes;
				}
			} else if (requiredBreakMinutes !== null) {
				breakMinutes = requiredBreakMinutes;
			} else if (hasStandardBreakRules && hasRequiredBreak) {
				breakMinutes = minPauseMinutesForCredit;
			} else {
				breakMinutes = workingHours?.minimumBreakDurationForCredit
					? Math.round(workingHours.minimumBreakDurationForCredit * 60)
					: 30;
			}

			breakMinutes = Math.max(breakMinutes, minPauseMinutesForCredit);
			endTime = addMinutes(startTime, breakMinutes);
		} else {
			endTime = addMinutes(startTime, 30);
		}

		endTime = clampDate(endTime, { start: startTime, end: dayEnd });

		return { startTime, endTime, type };
	}
}
